import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Plus, Mail, Phone, User, MapPin, ToggleLeft, ToggleRight, Trash2, Edit } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFlashMessage } from '../components/FlashMessage';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingSpinner from '../components/LoadingSpinner';
import API from '../services/api';

const OwnerUserManagement = () => {
  const { user } = useAuth();
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Confirm dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await API.get('/users');

      if (response.data.success) {
        setUsers(response.data.data);
      } else {
        setError(response.data.message || 'Kullanıcılar yüklenirken hata oluştu');
      }
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Kullanıcılar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleUserStatus = (userToModify) => {
    setUserToToggle(userToModify);
    setDialogOpen(true);
  };

  const confirmToggleStatus = async () => {
    if (!userToToggle) return;

    setActionLoading(true);

    try {
      const newStatus = !userToToggle.is_active;
      const response = await API.put(`/users/${userToToggle.id}/status`, {
        is_active: newStatus
      });

      if (response.data.success) {
        showSuccess(`${userToToggle.full_name} başarıyla ${newStatus ? 'aktif edildi' : 'pasif edildi'}`);
        setDialogOpen(false);
        setUserToToggle(null);
        loadUsers(); // Reload users
      } else {
        showDanger(response.data.message || 'İşlem başarısız');
      }
    } catch (error) {
      console.error('Toggle user status error:', error);
      showDanger('İşlem başarısız');
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleText = (locations) => {
    if (!locations || locations.length === 0) return 'Atanmamış';

    const roles = locations.map(loc => {
      switch (loc.role) {
        case 'owner': return 'Sahip';
        case 'staff': return 'Vale';
        default: return loc.role;
      }
    });

    return [...new Set(roles)].join(', ');
  };

  const getLocationText = (locations) => {
    if (!locations || locations.length === 0) return 'Atanmamış';
    return locations.map(loc => loc.name).join(', ');
  };

  if (!user || (user.user_type !== 'patron' && !user.isSystemAdmin)) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kullanıcı Yönetimi</h1>
          <p className="text-gray-600">İşletmenizin çalışanlarını yönetin</p>
        </div>
        <Link to="/owner/users/new" className="btn-primary flex items-center">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kullanıcı
        </Link>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Users list */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Kullanıcılar ({users.length})
          </h3>
        </div>
        <div className="card-body">
          {users.length > 0 ? (
            <>
              {/* Desktop Table - Enterprise Grade with Visual Hierarchy */}
              <div className="hidden lg:block">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" style={{ tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '25%' }} /> {/* Kullanıcı - Primary Column */}
                        <col style={{ width: '22%' }} /> {/* İletişim - Important Metadata */}
                        <col style={{ width: '28%' }} /> {/* Rol & Lokasyon - Main Content */}
                        <col style={{ width: '12%' }} /> {/* Durum - Narrow Status */}
                        <col style={{ width: '13%' }} /> {/* İşlemler - Fixed Action */}
                      </colgroup>
                      <thead>
                        <tr className="bg-gray-50">
                          <th scope="col" className="px-6 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Kullanıcı
                          </th>
                          <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            İletişim
                          </th>
                          <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Rol & Lokasyon
                          </th>
                          <th scope="col" className="px-5 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Durum
                          </th>
                          <th scope="col" className="px-6 py-3.5 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            İşlemler
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {users.map((userItem) => (
                          <tr key={userItem.id} className="hover:bg-gray-50 transition-colors duration-150">
                            {/* Kullanıcı - Primary Column with Avatar */}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-gray-100 to-gray-50 rounded-full flex items-center justify-center border border-gray-200">
                                  <User className="h-5 w-5 text-gray-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold text-gray-900 truncate">
                                    {userItem.full_name}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate">
                                    @{userItem.username}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* İletişim - Metadata Column */}
                            <td className="px-5 py-4">
                              <div className="space-y-1.5">
                                {userItem.email && (
                                  <div className="flex items-center gap-1.5 text-sm text-gray-700">
                                    <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    <span className="truncate">{userItem.email}</span>
                                  </div>
                                )}
                                {userItem.phone && (
                                  <div className="flex items-center gap-1.5 text-sm text-gray-700 tabular-nums">
                                    <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                    <span>{userItem.phone}</span>
                                  </div>
                                )}
                                {!userItem.email && !userItem.phone && (
                                  <span className="text-sm text-gray-400 italic">Belirtilmemiş</span>
                                )}
                              </div>
                            </td>

                            {/* Rol & Lokasyon - Main Content Column */}
                            <td className="px-5 py-4">
                              <div className="space-y-1.5">
                                <div className="text-sm font-semibold text-gray-900">
                                  {getRoleText(userItem.locations)}
                                </div>
                                <div className="flex items-start gap-1.5 text-sm text-gray-600">
                                  <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                                  <span className="line-clamp-2 leading-5">
                                    {getLocationText(userItem.locations)}
                                  </span>
                                </div>
                              </div>
                            </td>

                            {/* Durum - Narrow Status Column */}
                            <td className="px-5 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
                                userItem.is_active
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {userItem.is_active ? 'Aktif' : 'Pasif'}
                              </span>
                            </td>

                            {/* İşlemler - Fixed Action Column */}
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end gap-3">
                                <Link
                                  to={`/owner/users/edit/${userItem.id}`}
                                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7a2c2c] hover:text-[#5a1f1f] transition-colors"
                                  title="Düzenle"
                                >
                                  <Edit className="h-4 w-4" />
                                </Link>
                                <button
                                  onClick={() => handleToggleUserStatus(userItem)}
                                  className={`transition-colors ${
                                    userItem.is_active
                                      ? 'text-green-600 hover:text-green-700'
                                      : 'text-gray-400 hover:text-gray-500'
                                  }`}
                                  title={userItem.is_active ? 'Pasif et' : 'Aktif et'}
                                >
                                  {userItem.is_active ? (
                                    <ToggleRight className="h-7 w-7" />
                                  ) : (
                                    <ToggleLeft className="h-7 w-7" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {users.length === 0 && (
                  <div className="text-center py-12 px-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                      <Users className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">Kullanıcı bulunamadı</h3>
                    <p className="text-sm text-gray-500">Henüz eklenmiş kullanıcı bulunmuyor</p>
                  </div>
                )}
              </div>
            </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-4">
                {users.map((userItem) => (
                  <div key={userItem.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-gray-100 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {userItem.full_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{userItem.username}
                          </div>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        userItem.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {userItem.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>

                    <div className="mt-3 space-y-2">
                      {userItem.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="h-4 w-4 mr-2" />
                          {userItem.email}
                        </div>
                      )}
                      {userItem.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 mr-2" />
                          {userItem.phone}
                        </div>
                      )}
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2" />
                        {getRoleText(userItem.locations)} - {getLocationText(userItem.locations)}
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => handleToggleUserStatus(userItem)}
                        className="btn-secondary text-sm flex items-center"
                      >
                        {userItem.is_active ? (
                          <>
                            <ToggleLeft className="h-4 w-4 mr-1" />
                            Pasif Et
                          </>
                        ) : (
                          <>
                            <ToggleRight className="h-4 w-4 mr-1" />
                            Aktif Et
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Henüz kullanıcı bulunmuyor</h3>
              <p className="text-gray-600 mb-4">
                İlk çalışanınızı ekleyerek başlayın.
              </p>
              <Link to="/owner/users/new" className="btn-primary">
                İlk Kullanıcıyı Ekle
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={confirmToggleStatus}
        title={userToToggle?.is_active ? "Kullanıcıyı Pasif Et" : "Kullanıcıyı Aktif Et"}
        message={`"${userToToggle?.full_name}" adlı kullanıcıyı ${userToToggle?.is_active ? 'pasif etmek' : 'aktif etmek'} istediğinizden emin misiniz?`}
        confirmText={userToToggle?.is_active ? "Pasif Et" : "Aktif Et"}
        cancelText="İptal"
        type={userToToggle?.is_active ? "warning" : "info"}
        loading={actionLoading}
      />
    </div>
  );
};

export default OwnerUserManagement;